// Robust Stripe loader with error handling and fallbacks
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getStripeKey, getStripeKeyForUser, getStripeTimeout } from './stripe-config';

// Cache Stripe instances by publishable key to avoid reloading
const stripeCache: Record<string, Promise<Stripe | null>> = {};
let stripeLoadAttempts = 0;
const maxRetries = 3;

// Default (environment-based) Stripe promise for backward compatibility
let defaultStripePromise: Promise<Stripe | null> | null = null;

/**
 * Load Stripe with the default environment-based key.
 * Used when no user email is available yet.
 */
export const loadStripeWithRetry = async (): Promise<Stripe | null> => {
  if (defaultStripePromise) {
    return defaultStripePromise;
  }

  const key = getStripeKey();
  defaultStripePromise = loadStripeForKey(key);
  return defaultStripePromise;
};

/**
 * Load Stripe with the appropriate key for a specific user.
 * Test users get the sandbox key even in production.
 */
export const loadStripeForUser = async (email: string | null | undefined): Promise<Stripe | null> => {
  const key = getStripeKeyForUser(email);
  return loadStripeForKey(key);
};

/**
 * Load Stripe for a given publishable key, with caching and retries.
 */
const loadStripeForKey = async (stripeKey: string): Promise<Stripe | null> => {
  // Return cached promise if we already started loading for this key
  if (stripeKey in stripeCache) {
    return stripeCache[stripeKey];
  }

  const promise = loadStripeWithFallback(stripeKey);
  stripeCache[stripeKey] = promise;
  return promise;
};

const loadStripeWithFallback = async (stripeKey: string): Promise<Stripe | null> => {
  try {
    const isTest = stripeKey.startsWith('pk_test_');
    console.log('Loading Stripe (' + (isTest ? 'TEST' : 'LIVE') + ' mode)...', new Date());

    // Create a timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Stripe loading timeout')), getStripeTimeout());
    });

    // Race between Stripe loading and timeout
    const stripe = await Promise.race([
      loadStripe(stripeKey),
      timeoutPromise
    ]);

    if (stripe) {
      console.log('Stripe loaded successfully (' + (isTest ? 'TEST' : 'LIVE') + ' mode)');
      stripeLoadAttempts = 0; // Reset on success
      return stripe;
    } else {
      throw new Error('Stripe returned null');
    }

  } catch (error) {
    console.error('Stripe loading failed:', error);
    stripeLoadAttempts++;

    if (stripeLoadAttempts < maxRetries) {
      console.log('Retrying Stripe load (attempt ' + (stripeLoadAttempts + 1) + '/' + maxRetries + ')...');
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, 1000 * stripeLoadAttempts));
      delete stripeCache[stripeKey]; // Reset cache for retry
      return loadStripeWithFallback(stripeKey);
    } else {
      console.error('Stripe failed to load after all retries');
      stripeLoadAttempts = 0;
      return null;
    }
  }
};

// Reset function for manual retry
export const resetStripeLoader = () => {
  // Clear all cached promises
  Object.keys(stripeCache).forEach(key => delete stripeCache[key]);
  defaultStripePromise = null;
  stripeLoadAttempts = 0;
};

// Retry function for manual retry
export const retryStripeLoad = async (): Promise<Stripe | null> => {
  resetStripeLoader();
  return loadStripeWithRetry();
};

// Check if Stripe is available
export const isStripeAvailable = async (): Promise<boolean> => {
  try {
    const stripe = await loadStripeWithRetry();
    return stripe !== null;
  } catch {
    return false;
  }
};
